/*
 * Cart rules - Lemon Straps
 *
 * Every rule (set up as a block in the theme editor) adds a free product when its
 * condition is met. Items added here carry a hidden line property "_auto" = rule id,
 * so they are never mixed up with items the customer added themselves.
 *
 * Removable rules (e.g. resizing tool):
 *   - more free items than qualifying items  -> remove the extras
 *   - qualifying items go up                 -> add the difference
 *   - customer deletes a free item           -> leave it (not added back)
 * Locked rules: the free quantity always equals what the rule says.
 * Prices come from Shopify automatic discounts.
 * Cart-value gifts are judged on the cart value BEFORE discount codes (original price),
 * so a code such as EARLY20 kept in the cart never removes a gift (2026-10-30).
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('CartRulesConfig');
  if (!cfgEl) return;
  var CFG;
  try {
    CFG = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }
  if (!CFG.enabled) return;
  var RULES = (CFG.rules || []).filter(function (r) { return r.product; });

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var RATE = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
  var CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var MEMORY_KEY = 'cartRules:memory';
  var TAGS_KEY = 'cartRules:tags';

  var origFetch = window.fetch.bind(window);
  var lastCart = null;
  var syncing = false;
  var pending = false;
  var timer = null;
  var popupOpen = false;
  var popupQueue = []; // several gifts unlocked at once: next popup opens when the previous one is done
  var holdRender = false;

  /* ---------- helpers ---------- */

  function load(key, store) {
    try {
      return JSON.parse(store.getItem(key) || '{}');
    } catch (e) {
      return {};
    }
  }
  function save(key, store, v) {
    try {
      store.setItem(key, JSON.stringify(v));
    } catch (e) {}
  }

  function threshold(rule) {
    if (rule.thresholdLocal !== null && rule.thresholdLocal !== undefined) return Number(rule.thresholdLocal);
    return Math.round(Number(rule.thresholdBase || 0) * RATE);
  }

  // Cart value for gift goals: paid items at their price before any discount code.
  function giftBase(base) {
    return base.reduce(function (s, i) {
      return s + (i.original_line_price != null ? i.original_line_price : i.final_line_price);
    }, 0);
  }

  function formatMoney(cents) {
    var amount = cents / 100;
    var whole = Math.abs(amount - Math.round(amount)) < 0.005;
    try {
      return new Intl.NumberFormat(CFG.locale || undefined, {
        style: 'currency',
        currency: CFG.currency,
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      }).format(whole ? Math.round(amount) : amount);
    } catch (e) {
      return (whole ? Math.round(amount) : amount.toFixed(2)) + ' ' + CFG.currency;
    }
  }

  function api(path, body) {
    return origFetch(ROOT + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    }).then(function (r) {
      return r.json();
    });
  }

  function availableVariants(product) {
    if (!product || !product.variants) return [];
    return product.variants.filter(function (v) { return v.available; });
  }

  function escapeHTML(s) {
    var d = document.createElement('div');
    d.textContent = s || '';
    return d.innerHTML;
  }

  // Free products this code manages. A line counts as managed if it carries our hidden
  // _auto property, or if it is one of the rules' free products without it (e.g. after checkout).
  var FREE_VARIANTS = {};
  RULES.forEach(function (r) {
    (r.product.variants || []).forEach(function (v) { if (!FREE_VARIANTS[v.id]) FREE_VARIANTS[v.id] = r.id; });
  });
  function ruleOf(item) {
    if (item.properties && item.properties._auto) return item.properties._auto;
    return FREE_VARIANTS[item.variant_id] || null;
  }
  function isManaged(item) {
    return !!ruleOf(item);
  }

  /* ---------- product tags (only fetched if a rule uses a tag) ---------- */

  function loadTags(cart) {
    var needTags = RULES.some(function (r) { return r.trigger === 'products' && r.tag; });
    if (!needTags) return Promise.resolve({});
    var cache = load(TAGS_KEY, sessionStorage);
    var tagRules = RULES.filter(function (r) { return r.trigger === 'products' && r.tag; });
    var missing = [];
    cart.items.forEach(function (i) {
      if (isManaged(i) || cache[i.handle] || missing.indexOf(i.handle) !== -1) return;
      // no need to look up tags if every tag rule already lists this product
      var listedEverywhere = tagRules.every(function (r) { return (r.productIds || []).indexOf(i.product_id) !== -1; });
      if (!listedEverywhere) missing.push(i.handle);
    });
    return Promise.all(
      missing.map(function (h) {
        return api('products/' + h + '.js')
          .then(function (p) {
            cache[h] = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
          })
          .catch(function () {
            cache[h] = [];
          });
      })
    ).then(function () {
      save(TAGS_KEY, sessionStorage, cache);
      return cache;
    });
  }

  /* ---------- rules ---------- */

  // How many free items each rule asks for right now
  function wanted(rule, base, tags) {
    if (rule.trigger === 'order_value') {
      if (!base.length) return 0;
      var total = giftBase(base);
      var t = threshold(rule);
      var met = rule.compare === 'more_than' ? total > t : total >= t;
      return met ? 1 : 0;
    }
    var ids = (rule.productIds || []).map(String);
    var count = base.reduce(function (s, i) {
      var byId = ids.indexOf(String(i.product_id)) !== -1;
      var byTag = rule.tag && tags[i.handle] && tags[i.handle].indexOf(rule.tag) !== -1;
      return s + (byId || byTag ? i.quantity : 0);
    }, 0);
    if (rule.quantity === 'per_order') return count > 0 ? 1 : 0;
    return count;
  }

  function reconcile(cart, tags) {
    var base = cart.items.filter(function (i) { return !isManaged(i); });
    var memory = load(MEMORY_KEY, localStorage);
    if (memory.token !== cart.token) memory = { token: cart.token, prev: {} };

    var updates = {};
    var adds = [];
    var popups = [];

    RULES.forEach(function (rule) {
      var lines = cart.items.filter(function (i) { return ruleOf(i) === rule.id; });
      var have = lines.reduce(function (s, i) { return s + i.quantity; }, 0);
      var want = wanted(rule, base, tags);
      var prev = memory.prev[rule.id] || 0;
      var target;

      if (!rule.removable) {
        target = want;
      } else if (have > want) {
        target = want; // fewer qualifying products: remove extras
      } else if (want > prev) {
        target = Math.min(want, have + (want - prev)); // more qualifying products: add the difference
      } else {
        target = have; // customer removed it: leave it
      }
      memory.prev[rule.id] = want;

      if (target === have && lines.length <= 1) return;

      if (lines.length) {
        // keep everything on one line
        lines.forEach(function (l, idx) { updates[l.key] = idx === 0 ? target : 0; });
        return;
      }
      if (target <= 0) return;
      var variants = availableVariants(rule.product);
      if (!variants.length) return;
      if (variants.length === 1) {
        adds.push({ id: variants[0].id, quantity: target, properties: { _auto: rule.id } });
      } else if (!popupOpen) {
        popups.push({ rule: rule, quantity: target });
      }
    });

    save(MEMORY_KEY, localStorage, memory);

    var onCartPage = /\/cart\/?$/.test(window.location.pathname);
    var withSections = function (body) {
      if (!onCartPage) body.sections = 'cart-drawer,cart-icon-bubble';
      return body;
    };
    var jobs = [];
    if (Object.keys(updates).length) jobs.push(function () { return api('cart/update.js', withSections({ updates: updates })); });
    if (adds.length) jobs.push(function () { return api('cart/add.js', withSections({ items: adds })); });

    var sections = null;
    return jobs
      .reduce(function (p, job) {
        return p.then(job).then(function (res) {
          if (res && res.sections) sections = res.sections;
        });
      }, Promise.resolve())
      .then(function () {
        return { changed: jobs.length > 0, popups: popups, sections: sections };
      });
  }

  /* ---------- sync loop ---------- */

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, 20);
  }

  function sync() {
    if (syncing) {
      pending = true;
      return Promise.resolve();
    }
    syncing = true;
    var result;
    return api('cart.js')
      .then(function (cart) {
        lastCart = cart;
        return loadTags(cart).then(function (tags) {
          return reconcile(cart, tags);
        });
      })
      .then(function (res) {
        result = res;
        if (res.changed) {
          if (res.sections) {
            // show the new drawer straight away, then load the cart
            holdRender = true;
            applySections(res.sections);
            return api('cart.js').then(function (cart) {
              lastCart = cart;
              holdRender = false;
            });
          }
          return api('cart.js').then(function (cart) {
            lastCart = cart;
            return refreshUI();
          });
        }
      })
      .catch(function (e) {
        console.warn('[cart-rules]', e);
      })
      .then(function () {
        syncing = false;
        holdRender = false;
        renderAll();
        if (result && result.popups && result.popups.length) {
          popupQueue = result.popups.slice(1);
          openVariantPopup(result.popups[0]);
        }
        if (pending) {
          pending = false;
          schedule();
        }
      });
  }

  function refreshUI() {
    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return Promise.resolve();
    }
    return origFetch(ROOT + '?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(applySections);
  }

  function applySections(sections) {
    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }
    var parser = new DOMParser();
    var drawer = document.querySelector('cart-drawer');
    if (drawer && sections['cart-drawer']) {
      var doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      var newInner = doc.querySelector('.drawer__inner');
      var oldInner = drawer.querySelector('.drawer__inner');
      if (newInner && oldInner) oldInner.innerHTML = newInner.innerHTML;
      var newDrawer = doc.querySelector('cart-drawer');
      if (newDrawer) drawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
    }
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      var bdoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      var sec = bdoc.querySelector('.shopify-section');
      if (sec) bubble.innerHTML = sec.innerHTML;
    }
  }

  /* ---------- goal bar (free shipping + any rule marked 'Show this goal on the free shipping bar') ---------- */

  // Each goal carries its own current value: free shipping = cart total after discounts
  // (what checkout uses), gift goals = cart value before discount codes.
  function barGoals(cart) {
    var giftTotal = giftBase(cart.items.filter(function (i) { return !isManaged(i); }));
    var goals = [{ at: threshold(CFG.bar), cur: cart.total_price, before: CFG.bar.textBefore, after: CFG.bar.textAfter, label: CFG.bar.label || 'Free shipping' }];
    RULES.forEach(function (r) {
      if (r.trigger === 'order_value' && r.showInBar) goals.push({ at: threshold(r), cur: giftTotal, before: r.barBefore, after: r.barAfter, label: r.barLabel || 'Free gift' });
    });
    return goals.filter(function (g) { return g.at > 0; }).sort(function (x, y) { return x.at - y.at; });
  }

  function barHTML(cart) {
    var bar = CFG.bar;
    var goals = barGoals(cart);
    if (!goals.length) return '';
    var max = goals[goals.length - 1].at;
    var next = goals.filter(function (g) { return g.cur < g.at; })[0];
    var text = next
      ? next.before.replace('[amount]', '<span class="cr-bar__amount">' + formatMoney(next.at - next.cur) + '</span>')
      : goals[goals.length - 1].after;
    var pct = Math.max(0, Math.min(100, (goals[goals.length - 1].cur / max) * 100));
    var multi = goals.length > 1;
    var marks = '';
    var labels = '';
    if (multi) {
      // Several goals: markers evenly spaced so every name fits under its marker.
      // The fill moves through each step in proportion to that step's amount.
      var n = goals.length;
      var step = 0;
      var prevAt = 0;
      for (var gi = 0; gi < n; gi++) {
        var g0 = goals[gi];
        if (g0.cur >= g0.at) {
          step = gi + 1;
          prevAt = g0.at;
          continue;
        }
        step = gi + Math.max(0, g0.cur - prevAt) / Math.max(1, g0.at - prevAt);
        break;
      }
      pct = Math.max(0, Math.min(100, (step / n) * 100));
      goals.forEach(function (g, i) {
        var left = ((i + 1) / n) * 100;
        var done = g.cur >= g.at ? ' is-done' : '';
        marks += '<span class="cr-bar__mark' + done + '" style="left:' + left + '%"></span>';
        labels +=
          '<span class="cr-bar__label' + done + (i === n - 1 ? ' is-last' : '') + '" style="left:' + left + '%;max-width:' + 100 / n + '%">' +
          '<span class="cr-bar__label-name">' + escapeHTML(g.label) + '</span>' +
          '<span class="cr-bar__label-at">' + formatMoney(g.at) + '</span>' +
          '</span>';
      });
    }
    return (
      '<div class="cr-bar' + (multi ? ' cr-bar--multi' : '') + '" style="--cr-track:' + bar.trackColor + ';--cr-fill:' + bar.fillColor + '">' +
      '<p class="cr-bar__text">' + text + '</p>' +
      '<div class="cr-bar__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + Math.round(pct) + '">' +
      '<div class="cr-bar__fill" style="width:' + pct + '%"></div>' +
      marks +
      '</div>' +
      (labels ? '<div class="cr-bar__labels" aria-hidden="true">' + labels + '</div>' : '') +
      '</div>'
    );
  }

  function renderBars() {
    if (!CFG.bar.enabled || !lastCart) return;
    var html = lastCart.item_count ? barHTML(lastCart) : ''; // no bar when the bag is empty
    var stamp = lastCart.total_price + '|' + html.length;
    document.querySelectorAll('cart-drawer .drawer__header').forEach(function (header) {
      placeBar(header, html, stamp);
    });
    var cartPage = document.querySelector('cart-items .title-wrapper-with-link');
    if (cartPage) placeBar(cartPage, html, stamp);
  }

  function placeBar(anchor, html, stamp) {
    var next = anchor.nextElementSibling;
    var holder = next && next.hasAttribute('data-cart-rules-bar') ? next : null;
    if (!holder) {
      holder = document.createElement('div');
      holder.setAttribute('data-cart-rules-bar', '');
      anchor.insertAdjacentElement('afterend', holder);
    }
    if (holder.getAttribute('data-stamp') !== stamp) {
      holder.innerHTML = html;
      holder.setAttribute('data-stamp', stamp);
    }
  }

  /* ---------- drawer touches: title, free items, fixed quantity, lock items that can't be removed ---------- */

  function lockManagedRows() {
    if (!lastCart) return;
    var heading = document.querySelector('cart-drawer .drawer__heading');
    if (heading && heading.textContent !== 'Shopping bag') heading.textContent = 'Shopping bag';
    var removable = {};
    RULES.forEach(function (r) { if (r.removable) removable[r.id] = true; });
    var lockedIndex = {};
    var freeIndex = {};
    var autoIndex = {};
    lastCart.items.forEach(function (i, idx) {
      if (isManaged(i)) autoIndex[idx + 1] = true;
      if (isManaged(i) && !removable[ruleOf(i)]) lockedIndex[idx + 1] = true;
      if (i.final_line_price === 0) freeIndex[idx + 1] = true;
    });
    document.querySelectorAll('cart-drawer .cart-item, cart-items .cart-item').forEach(function (row) {
      var input = row.querySelector('.quantity__input');
      var index = input && input.getAttribute('data-index');
      var qtyCell = row.querySelector('.cart-item__quantity');
      if (!qtyCell || !index) return;
      row.classList.toggle('cr-free', !!freeIndex[index]);
      row.classList.toggle('cr-auto', !!autoIndex[index]);
      if (lockedIndex[index]) {
        if (!qtyCell.querySelector('.cr-included')) {
          var label = document.createElement('span');
          label.className = 'cr-included caption-with-letter-spacing';
          label.textContent = CFG.includedLabel || 'Included';
          qtyCell.appendChild(label);
        }
        qtyCell.classList.add('cr-locked');
      } else {
        qtyCell.classList.remove('cr-locked');
        var old = qtyCell.querySelector('.cr-included');
        if (old) old.remove();
      }
    });
  }

  /* ---------- You may also like (complementary products of what's in the bag) ---------- */

  var UP = CFG.upsell || {};
  var upCache = {};
  var upState = { want: null, key: null, html: '', products: {}, prefs: [] };
  var COLOR_NEAR = {
    gold: ['gold', 'transparent', 'starlight'],
    silver: ['silver', 'transparent', 'starlight'],
    starlight: ['starlight', 'transparent', 'silver'],
    'rose gold': ['rose gold', 'transparent'],
    black: ['black', 'transparent', 'starlight'],
    'two tone': ['transparent', 'starlight', 'silver'],
  };

  function getJSON(url) {
    return origFetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  function recsFor(id) {
    if (!upCache[id]) {
      upCache[id] = getJSON(ROOT + 'recommendations/products.json?product_id=' + id + '&intent=complementary&limit=8').then(function (d) {
        return (d && d.products) || [];
      });
    }
    return upCache[id];
  }
  function productByHandle(h) {
    var k = 'h:' + h;
    if (!upCache[k]) upCache[k] = getJSON(ROOT + 'products/' + h + '.js');
    return upCache[k];
  }
  function cents(v) {
    if (typeof v === 'number') return v;
    var n = parseFloat(v);
    return isNaN(n) ? 0 : String(v).indexOf('.') !== -1 ? Math.round(n * 100) : n;
  }
  function imgUrl(src, w) {
    if (!src) return '';
    if (typeof src === 'object') src = src.src || '';
    var u = src.indexOf('//') === 0 ? 'https:' + src : src;
    try {
      var x = new URL(u, location.origin);
      x.searchParams.set('width', w);
      return x.href;
    } catch (e) {
      return u;
    }
  }
  function normColor(s) {
    return String(s || '').toLowerCase().replace(/18k|plated|plating/g, '').replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim();
  }
  function colorIndex(p) {
    var n = (p.options || []).map(function (o) { return String((o && o.name) || o).toLowerCase(); });
    var i = n.indexOf('color');
    if (i < 0) i = n.indexOf('colour');
    return i;
  }
  // colours to try first, from the most expensive band in the bag (gold band -> gold bumper)
  function bagColors(base) {
    var c = base
      .map(function (i) {
        var pt = String(i.product_title || '').split(' - ');
        return pt.length > 1 ? normColor(pt[pt.length - 1]) : '';
      })
      .filter(Boolean)[0];
    return c ? COLOR_NEAR[c] || [c, 'transparent', 'starlight'] : [];
  }
  function bestVariant(p, prefs) {
    var avail = (p.variants || []).filter(function (v) { return v.available; });
    if (!avail.length) return null;
    var ci = colorIndex(p);
    if (ci > -1) {
      for (var k = 0; k < prefs.length; k++) {
        var hit = avail.filter(function (v) { return normColor(v.options[ci]) === prefs[k]; })[0];
        if (hit) return hit;
      }
    }
    return avail[0];
  }
  // same naming as the product page: "Bright" + "Apple Watch Bumper · Gold"
  function shortName(t, col) {
    var pre = (String(t).match(/^Free Gift\s*[-:]\s*/i) || [''])[0];
    t = String(t).slice(pre.length);
    var pt = t.split(' - ');
    var n = pt[0].replace(/\s*\(\d+\s*Colou?rs?\)/i, '');
    var ty = '';
    var m = n.match(/\s*Apple Watch (Screen Protector|Bumper|Case|Bracelet|Strap|Band)\b/);
    if (m) {
      ty = 'Apple Watch ' + m[1];
      n = n.replace(m[0], ' ');
    } else {
      var r = n.match(/\s*\b(Ring|Necklace|Earrings?|Bracelet)\s*$/);
      if (r) {
        ty = r[1];
        n = n.replace(r[0], '');
      }
    }
    var steel = /Stainless Steel|St-Steel/.test(t);
    n = n.replace('(St-Steel) ', '').replace(' Stainless Steel', '').replace(/\s+/g, ' ').trim();
    var sub = ty ? (steel ? 'Stainless Steel ' : '') + ty : '';
    var c = col || (pt.length > 1 ? pt[pt.length - 1].trim() : '');
    if (c) sub = sub ? sub + ' · ' + c : c;
    return { n: (pre ? 'Free Gift - ' : '') + (n || t), s: sub };
  }

  // colour swatches: Shopify swatch colour if set on the option value, otherwise by name
  var SWATCH = {
    gold: '#d9b86c', silver: '#cfd0d2', starlight: '#ece4d6', 'rose gold': '#e8b9a8', black: '#1a1a1a',
    white: '#ffffff', 'pearl white': '#f6f3ec', pink: '#f3c4cf', 'light pink': '#f7d9de', blue: '#9cb8d8',
    'baby blue': '#c5dcf0', green: '#8fb59a', 'mint green': '#bfe3cf', grey: '#a7a7a7', red: '#c9463d',
    yellow: '#f2d16b', 'light blue': '#b9d6ee', lavender: '#cfc3e6', brown: '#8a5a3c', latte: '#c8a98a',
    caramel: '#b9783f', olive: '#7d7d4f', 'unicorn glow': 'linear-gradient(135deg,#f6c6de,#c7d8f6,#f8edb9)',
    'two tone': 'linear-gradient(135deg,#cfd0d2 50%,#d9b86c 50%)',
    transparent: 'repeating-conic-gradient(#e6e6e6 0 25%,#fff 0 50%) 50% / 10px 10px',
  };
  // 2026-11-03: when Lemon colour swatches are on (CFG.lsSwatches), names use the shared colour list
  // (CFG.lsSwatchMap, from snippets/ls-swatch-bg) and unknown colours use that colour's photo, same as product pages.
  function swatchFor(opt, val, p) {
    var k = opt.values.indexOf(val);
    if (opt.swatches && opt.swatches[k]) return opt.swatches[k];
    if (CFG.lsSwatches) {
      var map = CFG.lsSwatchMap || {};
      var raw = String(val || '').toLowerCase().trim();
      if (map[raw]) return map[raw];
      if (map[normColor(val)]) return map[normColor(val)];
      var hit = p && (p.variants || []).filter(function (v) { return v.options[opt.i] === val && v.featured_image; })[0];
      if (hit) return '#f4f4f4 url(' + imgUrl(hit.featured_image, 80) + ') center/cover no-repeat';
    }
    return SWATCH[normColor(val)] || null;
  }

  function upsellList(cart) {
    var base = cart.items
      .filter(function (i) { return !isManaged(i); })
      .sort(function (a, b) { return b.final_line_price - a.final_line_price; });
    if (!base.length) return Promise.resolve([]);
    var inBag = {};
    cart.items.forEach(function (i) { inBag[i.product_id] = 1; });
    RULES.forEach(function (r) { inBag[r.product.id] = 1; });
    var ids = [];
    base.forEach(function (i) { if (ids.indexOf(i.product_id) === -1) ids.push(i.product_id); });
    var limit = UP.limit || 4;
    // never suggest another watch band - suggestions should add to the band, not replace it
    var ok = function (p) { return p && p.available && !inBag[p.id] && !/band/i.test(p.type || p.product_type || ''); };
    return Promise.all(ids.slice(0, 3).map(recsFor)).then(function (lists) {
      var out = [];
      var seen = {};
      lists.forEach(function (l) {
        l.forEach(function (p) {
          if (!ok(p) || seen[p.id]) return;
          seen[p.id] = 1;
          out.push(p);
        });
      });
      if (out.length) return out.slice(0, limit);
      return Promise.all((UP.fallback || []).map(productByHandle)).then(function (ps) {
        return ps.filter(ok).slice(0, limit);
      });
    });
  }

  var PLUS_SVG = '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>';

  function upsellCard(p, prefs) {
    var v = bestVariant(p, prefs);
    var ci = colorIndex(p);
    var nm = shortName(p.title, v && ci > -1 ? v.options[ci] : '');
    var img = (v && v.featured_image && v.featured_image.src) || p.featured_image || (p.images && p.images[0]) || '';
    var url = (p.url || ROOT + 'products/' + p.handle) + (v ? (String(p.url || '').indexOf('?') > -1 ? '&' : '?') + 'variant=' + v.id : '');
    return (
      '<li class="cr-up__card">' +
      '<a class="cr-up__media" href="' + url + '" tabindex="-1" aria-hidden="true">' +
      (img ? '<img src="' + imgUrl(img, 240) + '" alt="" loading="lazy" width="84" height="84">' : '') +
      '</a>' +
      '<div class="cr-up__info">' +
      '<a class="cr-up__name" href="' + url + '">' + escapeHTML(nm.n) + '</a>' +
      (nm.s ? '<span class="cr-up__sub">' + escapeHTML(nm.s) + '</span>' : '') +
      '<span class="cr-up__price">' + formatMoney(cents(v ? v.price : p.price)) + '</span>' +
      '</div>' +
      '<button type="button" class="cr-up__add" data-up="' + p.id + '" aria-label="Add ' + escapeHTML(nm.n) + '">' + PLUS_SVG + '</button>' +
      '</li>'
    );
  }

  function placeUpsell() {
    var anchor = document.querySelector('cart-drawer cart-drawer-items');
    if (!anchor) return;
    var next = anchor.nextElementSibling;
    var holder = next && next.hasAttribute('data-cart-rules-upsell') ? next : null;
    if (!holder) {
      holder = document.createElement('div');
      holder.setAttribute('data-cart-rules-upsell', '');
      anchor.insertAdjacentElement('afterend', holder);
    }
    var key = upState.key || '';
    if (holder.getAttribute('data-key') !== key) {
      holder.innerHTML = upState.html;
      holder.setAttribute('data-key', key);
    }
    if (typeof renderGifts === 'function') renderGifts();
  }

  function renderUpsell() {
    if (!UP.enabled || !lastCart) return;
    var key = lastCart.item_count
      ? lastCart.items.map(function (i) { return i.product_id + (isManaged(i) ? 'f' : ''); }).join(',')
      : '';
    if (key !== upState.want) {
      upState.want = key;
      var cart = lastCart;
      (key ? upsellList(cart) : Promise.resolve([])).then(function (list) {
        if (upState.want !== key) return;
        var base = cart.items.filter(function (i) { return !isManaged(i); }).sort(function (a, b) { return b.final_line_price - a.final_line_price; });
        var prefs = bagColors(base);
        upState.prefs = prefs;
        upState.products = {};
        list.forEach(function (p) { upState.products[p.id] = p; });
        upState.html = list.length
          ? '<div class="cr-up"><p class="cr-up__title">' + escapeHTML(UP.heading || 'You may also like') + '</p>' +
            '<ul class="cr-up__list" role="list">' + list.map(function (p) { return upsellCard(p, prefs); }).join('') + '</ul></div>'
          : '';
        upState.key = key;
        placeUpsell();
      });
    }
    placeUpsell();
  }

  function addToBag(variantId, btn, props, qty) {
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    }
    var line = { id: Number(variantId), quantity: qty || 1 };
    if (props) line.properties = props;
    return api('cart/add.js', { items: [line], sections: 'cart-drawer,cart-icon-bubble' })
      .then(function (res) {
        if (res && res.sections) applySections(res.sections);
      })
      .catch(function (e) { console.warn('[cart-rules]', e); })
      .then(function () {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
        }
        schedule();
      });
  }

  // option picker for products with colours / sizes (suggestions and free gifts)
  // o: { title, free, properties, quantity }
  function openPicker(p, o) {
    o = o || {};
    if (popupOpen) return;
    var opts = (p.options || []).map(function (o, i) {
      return { name: (o && o.name) || String(o), values: (o && o.values) || [], swatches: (o && o.swatches) || null, i: i };
    });
    var variants = p.variants || [];
    var ci = colorIndex(p);
    var pre = bestVariant(p, upState.prefs);
    var sel = opts.map(function (o) {
      if (o.values.length === 1) return o.values[0];
      if (o.i === ci && pre && upState.prefs.length) return pre.options[ci];
      return null;
    });
    popupOpen = true;

    var modal = document.createElement('div');
    // free gifts slide up from the bottom of the screen as a full-height sheet
    modal.className = 'cr-modal' + (o.free ? ' cr-modal--sheet' : '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', o.title || p.title);
    document.body.appendChild(modal);

    function match(extra) {
      return variants.filter(function (v) {
        return opts.every(function (o) {
          var want = extra && extra.i === o.i ? extra.v : sel[o.i];
          return want == null || v.options[o.i] === want;
        });
      });
    }
    function chosen() {
      if (sel.some(function (x) { return x == null; })) return null;
      return match()[0] || null;
    }
    function draw() {
      var v = chosen();
      var show = v || match().filter(function (x) { return x.available; })[0] || pre || variants[0];
      var nm = shortName(p.title, show && ci > -1 && sel[ci] ? show.options[ci] : '');
      var img = (show && show.featured_image && show.featured_image.src) || p.featured_image || '';
      var missing = opts.filter(function (o) { return sel[o.i] == null; })[0];
      var label = missing
        ? 'Select ' + missing.name.toLowerCase()
        : v && v.available
          ? o.free ? 'Add free gift' : 'Add to bag · ' + formatMoney(cents(v.price))
          : 'Sold out';
      var priceHTML = o.free
        ? '<s>' + formatMoney(cents(show ? show.price : p.price)) + '</s> Free'
        : formatMoney(cents(show ? show.price : p.price));
      modal.innerHTML =
        '<div class="cr-modal__overlay" data-close></div>' +
        '<div class="cr-modal__box cr-pick">' +
        '<button type="button" class="cr-modal__close" data-close aria-label="Close">&times;</button>' +
        (o.title ? '<p class="cr-pick__title">' + escapeHTML(o.title) + '</p>' : '') +
        '<div class="cr-pick__head">' +
        '<div class="cr-pick__img">' + (img ? '<img src="' + imgUrl(img, 240) + '" alt="">' : '') + '</div>' +
        '<div><p class="cr-pick__name">' + escapeHTML(nm.n) + '</p>' +
        (nm.s ? '<p class="cr-pick__sub">' + escapeHTML(nm.s) + '</p>' : '') +
        '<p class="cr-pick__price">' + priceHTML + '</p></div>' +
        '</div>' +
        opts
          .filter(function (o) { return o.values.length > 1; })
          .map(function (o) {
            var isColor = o.i === ci && o.values.every(function (val) { return swatchFor(o, val, p); });
            return (
              '<fieldset class="cr-pick__opt"><legend>' + escapeHTML(o.name) +
              (sel[o.i] ? '<span>' + escapeHTML(sel[o.i]) + '</span>' : '') + '</legend><div class="cr-pick__vals' + (isColor ? ' cr-pick__vals--sw' : '') + '">' +
              o.values
                .map(function (val) {
                  var possible = match({ i: o.i, v: val }).some(function (x) { return x.available; });
                  var cls = 'cr-pick__val' + (isColor ? ' cr-pick__sw' : '') + (sel[o.i] === val ? ' is-on' : '');
                  return (
                    '<button type="button" class="' + cls + '" data-i="' + o.i + '" data-v="' + escapeHTML(val) + '"' +
                    (isColor ? ' style="--sw:' + escapeHTML(swatchFor(o, val, p)) + '" title="' + escapeHTML(val) + '" aria-label="' + escapeHTML(val) + '"' : '') +
                    (possible ? '' : ' disabled') + '>' + (isColor ? '' : escapeHTML(val)) + '</button>'
                  );
                })
                .join('') +
              '</div></fieldset>'
            );
          })
          .join('') +
        '<button type="button" class="cr-pick__add"' + (v && v.available ? '' : ' disabled') + '>' + escapeHTML(label) + '</button>' +
        '</div>';
    }
    function close() {
      popupOpen = false;
      document.removeEventListener('keydown', onKey);
      if (!o.free) return modal.remove();
      modal.classList.remove('is-open');
      setTimeout(function () {
        modal.remove();
        if (o.onClose) o.onClose();
      }, 350);
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    modal.addEventListener('click', function (e) {
      var val = e.target.closest('.cr-pick__val');
      if (val && !val.disabled) {
        var i = Number(val.getAttribute('data-i'));
        var v = val.getAttribute('data-v');
        sel[i] = sel[i] === v ? null : v;
        // drop choices that no longer exist with this selection
        opts.forEach(function (o) {
          if (o.i !== i && sel[o.i] != null && !match().some(function (x) { return x.available; })) sel[o.i] = null;
        });
        draw();
        return;
      }
      var add = e.target.closest('.cr-pick__add');
      if (add && !add.disabled) {
        var ch = chosen();
        if (!ch) return;
        add.disabled = true;
        addToBag(ch.id, null, o.properties, o.quantity).then(close);
        return;
      }
      if (e.target.closest('[data-close]')) close();
    });
    document.addEventListener('keydown', onKey);
    draw();
    if (o.free) requestAnimationFrame(function () { requestAnimationFrame(function () { modal.classList.add('is-open'); }); });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.cr-up__add');
    if (!btn) return;
    e.preventDefault();
    var p = upState.products[btn.getAttribute('data-up')];
    if (!p) return;
    var avail = (p.variants || []).filter(function (v) { return v.available; });
    if (!avail.length) return;
    if ((p.variants || []).length === 1) addToBag(avail[0].id, btn);
    else openPicker(p);
  });

  /* ---------- Gift section (cart-value gifts marked 'Show in the bag's gift section') ---------- */

  var GIFT_RULES = RULES.filter(function (r) { return r.trigger === 'order_value' && r.showInGifts; });

  function giftHTML(cart) {
    if (!GIFT_RULES.length || !cart.item_count) return '';
    var base = cart.items.filter(function (i) { return !isManaged(i); });
    if (!base.length) return '';
    var total = giftBase(base);
    var prefs = bagColors(base.slice().sort(function (a, b) { return b.final_line_price - a.final_line_price; }));
    // order: unlocked but not added, then locked, then added; lowest amount first in each group
    var list = GIFT_RULES.map(function (r) {
      var t = threshold(r);
      var met = r.compare === 'more_than' ? total > t : total >= t;
      var inBag = cart.items.some(function (i) { return ruleOf(i) === r.id; });
      return { r: r, t: t, met: met, inBag: inBag, group: met && !inBag ? 0 : !met ? 1 : 2 };
    });
    list.sort(function (a, b) { return a.group - b.group || a.t - b.t; });
    var cards = list.map(function (g) {
      var r = g.r;
      var p = r.product;
      var t = g.t;
      var met = g.met;
      var inBag = g.inBag;
      var v = bestVariant(p, prefs);
      var nm = shortName(p.title, '');
      var img = (v && v.featured_image && v.featured_image.src) || p.image || '';
      var left = Math.max(t - total, 0) + (r.compare === 'more_than' ? 1 : 0);
      // locked: "Add [amount] more to unlock" sits above the product name
      var msg = met ? '' : escapeHTML(r.giftText || 'Add [amount] more to unlock').replace('[amount]', formatMoney(left));
      var action = !met ? '' : inBag
        ? '<span class="cr-gift__added">Added</span>'
        : '<button type="button" class="cr-gift__btn" data-gift="' + escapeHTML(r.id) + '">Add</button>';
      return (
        '<li class="cr-up__card cr-gift__card' + (met ? ' is-met' : ' is-locked') + (action ? ' has-action' : '') + '">' +
        '<span class="cr-up__media">' + (img ? '<img src="' + imgUrl(img, 240) + '" alt="" loading="lazy" width="84" height="84">' : '') + '</span>' +
        '<div class="cr-up__info cr-gift__info">' +
        (msg ? '<span class="cr-gift__msg">' + msg + '</span>' : '') +
        '<span class="cr-up__name">' + escapeHTML(nm.n.replace(/^Free Gift - /, '')) + '</span>' +
        (nm.s ? '<span class="cr-up__sub">' + escapeHTML(nm.s) + '</span>' : '') +
        '<span class="cr-up__price"><s>' + formatMoney(cents(v ? v.price : p.price)) + '</s> FREE</span>' +
        '</div>' +
        action +
        '</li>'
      );
    });
    return '<div class="cr-up cr-gift"><p class="cr-up__title">' + escapeHTML(CFG.giftHeading || 'Gift') + '</p><ul class="cr-up__list" role="list">' + cards.join('') + '</ul></div>';
  }

  function renderGifts() {
    if (!lastCart || !GIFT_RULES.length) return;
    var anchor = document.querySelector('cart-drawer [data-cart-rules-upsell]') || document.querySelector('cart-drawer cart-drawer-items');
    if (!anchor) return;
    var html = giftHTML(lastCart);
    var next = anchor.nextElementSibling;
    var holder = next && next.hasAttribute('data-cart-rules-gifts') ? next : document.querySelector('cart-drawer [data-cart-rules-gifts]');
    if (!holder) {
      holder = document.createElement('div');
      holder.setAttribute('data-cart-rules-gifts', '');
    }
    if (anchor.nextElementSibling !== holder) anchor.insertAdjacentElement('afterend', holder);
    var stamp = 0;
    for (var k = 0; k < html.length; k++) stamp = (stamp * 31 + html.charCodeAt(k)) | 0;
    if (holder.getAttribute('data-stamp') !== String(stamp)) {
      holder.innerHTML = html;
      holder.setAttribute('data-stamp', String(stamp));
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.cr-gift__btn');
    if (!btn) return;
    e.preventDefault();
    var rule = RULES.filter(function (r) { return r.id === btn.getAttribute('data-gift'); })[0];
    if (!rule) return;
    var variants = availableVariants(rule.product);
    if (!variants.length) return;
    if (rule.product.variants.length === 1) addToBag(variants[0].id, btn, { _auto: rule.id }, 1);
    else openPicker(rule.product, { title: rule.popupTitle || 'Choose your free gift', free: true, properties: { _auto: rule.id }, quantity: 1 });
  });

  function renderAll() {
    if (holdRender) return;
    renderBars();
    lockManagedRows();
    renderUpsell();
    renderGifts();
  }

  /* ---------- free gift with several variants: customer picks colour / size ---------- */

  function openVariantPopup(job) {
    var rule = job.rule;
    // closing the popup counts as "no thanks" until the bag changes again
    openPicker(rule.product, { title: rule.popupTitle || 'Choose your free gift', free: true, properties: { _auto: rule.id }, quantity: job.quantity, onClose: nextPopup });
  }

  // after a gift is picked or its popup is closed, open the next unlocked gift (unless it's already in the bag)
  function nextPopup() {
    while (popupQueue.length) {
      var job = popupQueue.shift();
      var inBag = lastCart && lastCart.items.some(function (i) { return ruleOf(i) === job.rule.id; });
      if (!inBag) return openVariantPopup(job);
    }
  }

  /* ---------- watch every cart change (theme + other apps) ---------- */

  window.fetch = function (input, init) {
    var p = origFetch(input, init);
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (CART_URL_RE.test(url)) p.then(schedule, function () {});
    } catch (e) {}
    return p;
  };

  var xOpen = XMLHttpRequest.prototype.open;
  var xSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._crUrl = url;
    return xOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (CART_URL_RE.test(String(this._crUrl || ''))) this.addEventListener('loadend', schedule);
    return xSend.apply(this, arguments);
  };

  var mo = new MutationObserver(function () {
    clearTimeout(mo._t);
    mo._t = setTimeout(renderAll, 30);
  });

  function start() {
    document.querySelectorAll('cart-drawer, cart-items').forEach(function (el) {
      mo.observe(el, { childList: true, subtree: true });
    });
    sync();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) schedule();
  });
})();
